import React, { useEffect, useState } from 'react';
import {
  listIntakeTokens,
  createIntakeToken,
  setIntakeTokenActive,
  listInterestSubmissions,
} from '../utils/api';
import { useToast } from './Toast';
import { FaLink, FaCopy, FaPlus } from 'react-icons/fa';

const cardStyle = {
  maxWidth: '920px',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '14px',
  padding: '20px',
  marginBottom: '16px',
};

function buildSnippet(token, kind = 'interest') {
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'https://YOUR-REAL-HOST';
  const path =
    kind === 'broker'
      ? `/api/public/intake/${token}/broker`
      : `/api/public/intake/${token}/interest`;
  const url = `${origin}${path}`;
  return `// Website form → Real (keep your page; only change submit handler)
fetch('${url}', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    name: form.name,
    phone: form.phone,          // e.g. 05xxxxxxxx or 9665xxxxxxxx
    email: form.email || null,  // optional for events; recommended for project pages
    interest_type: form.intent || 'inquiry', // or purchase
    consent: true,              // required
    project_key: 'my-project',  // optional override
    source: 'web_interest',
    // broker endpoint instead uses: city, license, project_types, source broker_signup
  }),
});`;
}

const IntakeTab = ({ language = 'ar' }) => {
  const isRTL = language === 'ar';
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('Website intake');
  const [projectKey, setProjectKey] = useState('');
  const [projectName, setProjectName] = useState('');
  const [kind, setKind] = useState('both');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        listIntakeTokens(),
        listInterestSubmissions({ kind: 'interest', limit: 50 }),
      ]);
      setTokens(tRes.data?.tokens || []);
      setSubs(sRes.data?.submissions || []);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreate = async () => {
    setCreating(true);
    try {
      await createIntakeToken({
        kind,
        label,
        project_key: projectKey || undefined,
        project_name: projectName || undefined,
      });
      toast?.success?.(language === 'ar' ? 'تم إنشاء الرمز' : 'Token created');
      await load();
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast?.success?.(language === 'ar' ? 'تم النسخ' : 'Copied');
    } catch (_) {
      toast?.error?.('Copy failed');
    }
  };

  return (
    <div style={{ padding: '24px', direction: isRTL ? 'rtl' : 'ltr' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: '1.15rem', color: '#0f172a' }}>
        {language === 'ar' ? 'استقبال الموقع (النموذج عندكم → Real)' : 'Website intake (your form → Real)'}
      </h2>
      <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '0.9rem', maxWidth: 720 }}>
        {language === 'ar'
          ? 'نموذجكم يبقى على موقعكم. عند الإرسال تُرسل البيانات إلى Real عبر رمز عام. لا حاجة لنقل الصفحة إلى Real.'
          : 'Keep your form on your site. On submit, post to Real with a public token. No need to move the page into Real.'}
      </p>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#ea580c' }}>
          <FaPlus style={{ marginInlineEnd: 8 }} />
          {language === 'ar' ? 'إنشاء رمز استقبال' : 'Create intake token'}
        </h3>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '1fr 1fr', maxWidth: 640 }}>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {language === 'ar' ? 'التسمية' : 'Label'}
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          </label>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {language === 'ar' ? 'النوع' : 'Kind'}
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            >
              <option value="both">Interest + Broker</option>
              <option value="interest">Interest only</option>
              <option value="broker">Broker only</option>
            </select>
          </label>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            project_key
            <input
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value)}
              placeholder="e.g. olou-tower"
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          </label>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            project_name
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              style={{ display: 'block', width: '100%', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1' }}
            />
          </label>
        </div>
        <button
          type="button"
          onClick={onCreate}
          disabled={creating}
          style={{
            marginTop: 14,
            padding: '8px 16px',
            borderRadius: 8,
            border: 'none',
            background: '#ea580c',
            color: '#fff',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {creating ? '…' : language === 'ar' ? 'إنشاء' : 'Create'}
        </button>
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>
          <FaLink style={{ marginInlineEnd: 8, color: '#ea580c' }} />
          {language === 'ar' ? 'الرموز النشطة' : 'Tokens'}
        </h3>
        {loading && <p style={{ color: '#94a3b8' }}>…</p>}
        {!loading && tokens.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
            {language === 'ar' ? 'لا رموز بعد' : 'No tokens yet'}
          </p>
        )}
        {tokens.map((t) => (
          <div
            key={t.id}
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              padding: 12,
              marginBottom: 10,
              background: t.active ? '#fff' : '#f8fafc',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <strong>{t.label || '—'}</strong>
                <span style={{ marginInlineStart: 8, fontSize: '0.75rem', color: '#64748b' }}>
                  {t.kind} {t.active ? '' : '(off)'}
                </span>
                <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', marginTop: 6, wordBreak: 'break-all' }}>
                  {t.token}
                </div>
                {(t.project_key || t.project_name) && (
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>
                    {t.project_name || t.project_key}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <button
                  type="button"
                  onClick={() => copy(buildSnippet(t.token, 'interest'))}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  <FaCopy /> Interest snippet
                </button>
                <button
                  type="button"
                  onClick={() => copy(buildSnippet(t.token, 'broker'))}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  <FaCopy /> Broker snippet
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await setIntakeTokenActive(t.id, !t.active);
                    load();
                  }}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: '0.75rem' }}
                >
                  {t.active ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>
            <pre
              style={{
                marginTop: 10,
                padding: 10,
                background: '#0f172a',
                color: '#e2e8f0',
                borderRadius: 8,
                fontSize: '0.7rem',
                overflow: 'auto',
                maxHeight: 160,
              }}
            >
              {buildSnippet(t.token, t.kind === 'broker' ? 'broker' : 'interest')}
            </pre>
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '0.95rem' }}>
          {language === 'ar' ? 'آخر تسجيلات الاهتمام' : 'Recent interest submissions'}
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ textAlign: isRTL ? 'right' : 'left', color: '#64748b' }}>
                <th style={{ padding: 8 }}>When</th>
                <th style={{ padding: 8 }}>Name</th>
                <th style={{ padding: 8 }}>Phone</th>
                <th style={{ padding: 8 }}>Interest</th>
                <th style={{ padding: 8 }}>Project</th>
                <th style={{ padding: 8 }}>Client</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 8 }}>{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: 8 }}>{s.name}</td>
                  <td style={{ padding: 8 }}>{s.phone}</td>
                  <td style={{ padding: 8 }}>{s.interest_type || '—'}</td>
                  <td style={{ padding: 8 }}>{s.project_name || s.project_key || '—'}</td>
                  <td style={{ padding: 8 }}>#{s.client_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!subs.length && !loading && (
            <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
              {language === 'ar' ? 'لا تسجيلات بعد — اختبر الـ API من موقعهم أو Postman' : 'No submissions yet — test from their site or Postman'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntakeTab;
